// v0.4.88 — Locale-safe calendar popover.
//
// Pairs with `<DateInput>` (v0.4.37 Q2). The text input stays as-is
// (DD/MM/YYYY, locale-independent typing path) and gains a small
// calendar-icon button that toggles this popover. Operators get a
// visual picker without giving up the locale-safety guarantee:
//   - Rendering is fully in-house (we control every character on
//     screen — no `Date.prototype.toLocaleDateString()` anywhere).
//   - Date selection emits ISO `YYYY-MM-DD` to the same `onChange`
//     contract `<DateInput>` already exposes.
//   - Typing path is untouched — operator can still type the date
//     and ignore the popover entirely.
//
// Sign-off 2026-06-20 (option q).

import React, { useEffect, useMemo, useRef } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";


const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const WEEKDAY_LABELS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];


const pad2 = (n) => String(n).padStart(2, "0");
const isoOf = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;


// Build a 6-row × 7-col calendar matrix for the given (year, month)
// where Monday is column 0 (EU convention — matches DD/MM/YYYY).
// Cells outside the month are returned as `null` so the renderer
// can render a blank tile. 6 rows is the worst case for any month.
const buildMonthMatrix = (year, month0) => {
  const firstOfMonth = new Date(Date.UTC(year, month0, 1));
  // getUTCDay(): 0 = Sun. EU-Monday-first index: shift by -1, wrap.
  const fmDow = (firstOfMonth.getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  const rows = [];
  let day = 1 - fmDow;
  for (let r = 0; r < 6; r++) {
    const row = [];
    for (let c = 0; c < 7; c++) {
      if (day < 1 || day > daysInMonth) row.push(null);
      else row.push(day);
      day++;
    }
    rows.push(row);
  }
  return rows;
};


export const CalendarPopover = ({
  isoValue, onPick, onClose, testidPrefix = "calendar",
}) => {
  const { t } = useTranslation();
  // Default to month-of-current-value, else current month.
  const seed = useMemo(() => {
    if (isoValue) {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoValue);
      if (m) return { y: Number(m[1]), mo: Number(m[2]) - 1 };
    }
    const now = new Date();
    return { y: now.getUTCFullYear(), mo: now.getUTCMonth() };
  }, [isoValue]);
  const [view, setView] = React.useState(seed);
  useEffect(() => { setView(seed); }, [seed]);

  const matrix = useMemo(
    () => buildMonthMatrix(view.y, view.mo),
    [view.y, view.mo],
  );

  // Close-on-outside-click. Ref guards the popover body.
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);

  const stepMonth = (delta) => {
    let mo = view.mo + delta;
    let y = view.y;
    while (mo < 0) { mo += 12; y--; }
    while (mo > 11) { mo -= 12; y++; }
    setView({ y, mo });
  };

  return (
    <div
      ref={ref}
      data-testid={`${testidPrefix}-popover`}
      className="absolute z-50 mt-2 w-72 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl shadow-lg p-3"
    >
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          data-testid={`${testidPrefix}-prev-month`}
          onClick={() => stepMonth(-1)}
          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
          aria-label={t("ui.calendar.prev_month", { defaultValue: "Previous month" })}
        >
          <CaretLeft size={14} weight="bold" />
        </button>
        <div
          data-testid={`${testidPrefix}-view-label`}
          className="text-sm font-semibold text-slate-900 dark:text-slate-50 tabular-nums"
        >
          {t("ui.calendar.view_label", { month: MONTH_LABELS[view.mo], year: view.y, num: view.mo + 1, defaultValue: `${MONTH_LABELS[view.mo]} ${view.y}` })}
        </div>
        <button
          type="button"
          data-testid={`${testidPrefix}-next-month`}
          onClick={() => stepMonth(1)}
          className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
          aria-label={t("ui.calendar.next_month", { defaultValue: "Next month" })}
        >
          <CaretRight size={14} weight="bold" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAY_LABELS.map((w) => (
          <div
            key={w}
            className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 text-center tabular-nums"
          >
            {t(`ui.calendar.weekday_${w.toLowerCase()}`, { defaultValue: w })}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {matrix.flatMap((row, ri) =>
          row.map((d, ci) => {
            if (d === null) {
              return (
                <div
                  key={`${ri}-${ci}`}
                  className="h-7"
                  aria-hidden="true"
                />
              );
            }
            const iso = isoOf(view.y, view.mo, d);
            const isSelected = iso === isoValue;
            return (
              <button
                key={`${ri}-${ci}`}
                type="button"
                data-testid={`${testidPrefix}-day-${iso}`}
                onClick={() => { onPick?.(iso); }}
                className={`h-7 text-xs tabular-nums rounded ${
                  isSelected
                    ? "bg-teal-600 text-white font-semibold"
                    : "text-slate-700 dark:text-slate-200 hover:bg-teal-50 dark:hover:bg-teal-900/40"
                }`}
              >
                {d}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};


export default CalendarPopover;
